import { isActive } from '@/services/feature-flag-service'
import { locationToSlippyTileName } from '@/lib/math'
import { httpGetJson } from '@/lib/net'
import type Geographic from '@/lib/geographic'
import { getApolloClient } from '@/lib/apollo-client'
import {
  SEARCH_RESULT_FRAGMENT,
  type SearchResultItem,
} from '@onxmaps/search-web'
import gql from 'graphql-tag'

type AutocompleteResultAbout = {
  type: string
  id: string
  name: string
  disambiguatingDescription: string
  additionalType: string
}

export type AutocompleteQueryHighlight = Array<[number, number]>

export type AutocompleteResult = {
  query: string
  queryHighlight: AutocompleteQueryHighlight
  about: AutocompleteResultAbout | null
}

interface AutocompleteResponse {
  query: string
  result: AutocompleteResult[]
}

export type AutocompleteOptions = {
  query: string
  location?: Geographic
  limit?: number
  withinStates?: string
  withinScopes?: string
}

const AUTOCOMPLETE_ENDPOINT = '/search/v1/autocomplete'

/** Determines if an AutocompleteRResult is a Knowledge Graph result */
export const isAutocompleteKgResult = (result: AutocompleteResult): boolean =>
  !!(result.about && result.about.type && result.about.type !== 'SearchResult')

/** Determines if an AutocompleteResult is a Search result */
export const isAutocompleteSearchResult = (
  result: AutocompleteResult
): boolean =>
  !!(result.about && result.about.type && result.about.type === 'SearchResult')

/**
 * Fetch results from the Autocomplete API for a given query and location.
 */
export const fetchAutocompleteResults = async ({
  query,
  location,
  limit = 10,
  withinStates,
  withinScopes,
}: AutocompleteOptions): Promise<AutocompleteResponse> => {
  let queryParams = `?q=${query}&limit=${limit}`

  // Add optional nearTile parameter
  if (location) {
    const { z, x, y } = locationToSlippyTileName(
      location.latitude,
      location.longitude,
      13
    )
    queryParams += `&nearTile=${z},${x},${y}`
  }

  // Add optional parameters if they are provided
  if (withinStates) {
    queryParams += `&withinStates=${withinStates}`
  }
  if (withinScopes) {
    queryParams += `&withinScopes=${withinScopes}`
  }

  return await httpGetJson<AutocompleteResponse>(
    `${AUTOCOMPLETE_ENDPOINT}${queryParams}`
  )
}

export type AutocompleteResolutionResponse = {
  autocompleteResult: {
    result: SearchResultItem
  }
}

/**
 * Fetches the data for a specific autocomplete result item.
 */
export const fetchAutocompleteResultItemData = async ({
  query,
  type,
  id,
}: {
  query: string
  type: string
  id: string
}) => {
  const AUTOCOMPLETE_RESULT_QUERY = gql`
    query autocompleteResolutionQuery($filter: AutocompleteResultFilter) {
      autocompleteResult(filter: $filter) {
        result {
          about {
            __typename
            ... on Trailhead {
              id
            }
            ... on Lake {
              id
            }
            ... on HikeRoute {
              id
            }
            ... on BikeRoute {
              id
            }
            ... on SnowRoute {
              id
            }
            ... on OffroadRoute {
              id
            }
            ... on SnowmobileRoute {
              id
            }
            ... on WaterAccessSite {
              id
            }
            ... on FishingArea {
              id
            }
          }
          ...SearchResultFragment
        }
      }
    }
    ${SEARCH_RESULT_FRAGMENT}
  `

  const client = getApolloClient()
  const response = await client.query<AutocompleteResolutionResponse>({
    query: AUTOCOMPLETE_RESULT_QUERY,
    variables: {
      filter: {
        q: query,
        about: {
          id: {
            eq: id,
          },
          type: {
            eq: type,
          },
        },
      },
    },
  })

  return response.data.autocompleteResult.result
}

/**
 * Checks if the search autocomplete feature is enabled.
 * @returns True if the feature is enabled, false otherwise.
 */
export const isSearchAutocompleteEnabled = (): boolean => {
  return !!isActive('fox_web_autocomplete')
}
